import React, { useState, useEffect } from "react";
import { Drawer, Form, Input, Select, AutoComplete, Button, Switch, message } from "antd";
import { UpOutlined, DownOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { ApiError, toUserMessages, klaviyoPicklists, useBlustreamApiQuery } from "../../../apiService/blustreamApi";
import {
  createEventMapping,
  updateEventMapping,
} from "../../../services/integrations/klaviyoEventMappingsService";
import PicklistLoadState from "./PicklistLoadState";

const { Option } = Select;

// CanonicalEventTypes (galadriel) is deliberately NOT an enum -- adding a new
// event type should never require a redeploy here. These three seed the
// combobox with the well-known Polly events, but the field stays free text
// so an existing tenant's non-seeded type (e.g. polly.nps_recorded) still
// round-trips.
const KNOWN_EVENT_TYPES = [
  { value: "polly.conversation_closed" },
  { value: "polly.sentiment_computed" },
  { value: "polly.question_asked" },
];

const SUPPORTED_ACTION_TYPES = new Set(["WRITE_ATTRIBUTE", "EMIT_EVENT"]);

const DEFAULT_DELAY_MS = 0;
const DEFAULT_RETRY_ATTEMPTS = 3;
const DEFAULT_RETRY_INTERVAL_MS = 1000;

const emptyStep = () => ({
  actionType: "EMIT_EVENT",
  vendorActionName: "",
  payloadSpec: [{ key: "", value: "" }],
  providerField: "",
  valuePath: "",
  delayMs: DEFAULT_DELAY_MS,
  retryAttempts: DEFAULT_RETRY_ATTEMPTS,
  retryIntervalMs: DEFAULT_RETRY_INTERVAL_MS,
  stepEnabled: true,
});

/**
 * The editor can only faithfully round-trip an optional run of WRITE_ATTRIBUTE
 * steps followed by exactly one EMIT_EVENT step, because that's the only
 * shape its UI knows how to construct. Loading anything else (two EMIT_EVENT
 * steps, an EMIT_EVENT ahead of a WRITE_ATTRIBUTE, an unrecognised
 * action_type) and still offering Save would flatten it back into a
 * supported shape on the next save -- silently losing steps, which is
 * exactly what AC 6 forbids. Real tenant data has ordered sequences like
 * this (Damien's client: WRITE_ATTRIBUTE then EMIT_EVENT), so this isn't
 * a hypothetical.
 */
function isEditableShape(steps) {
  if (!Array.isArray(steps) || steps.length === 0) return true;
  let seenEmitEvent = false;
  for (const step of steps) {
    if (!SUPPORTED_ACTION_TYPES.has(step.action_type)) return false;
    if (seenEmitEvent) return false;
    if (step.action_type === "EMIT_EVENT") seenEmitEvent = true;
  }
  return seenEmitEvent;
}

export default function EventMappingDrawer({ visible, onClose, onSaved, editMapping }) {
  const [form] = Form.useForm();
  const [steps, setSteps] = useState([emptyStep()]);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendorActionNameError, setVendorActionNameError] = useState(null);
  const [payloadFieldErrors, setPayloadFieldErrors] = useState({});
  const [metricsRetryKey, setMetricsRetryKey] = useState(0);
  const [metricPropertiesRetryKey, setMetricPropertiesRetryKey] = useState(0);
  const isEdit = !!editMapping;
  const unsupportedShape = isEdit && !isEditableShape(editMapping.steps);

  // Metrics are authoritative (Klaviyo's own event names), but sourced only
  // while the drawer is open -- fetched once for the whole drawer, not per
  // step, since every EMIT_EVENT step shares the same tenant-level list.
  const { data: metrics, error: metricsError } = useBlustreamApiQuery(
    (signal) => (visible ? klaviyoPicklists.getMetrics({ signal }) : Promise.resolve(null)),
    [visible, metricsRetryKey]
  );
  const metricOptions = (metrics || []).map((m) => ({ value: m.name }));

  // The editable shape allows exactly one EMIT_EVENT step (see
  // isEditableShape above), so its vendor action name is the single source
  // for which metric's properties to suggest. `find` (not the mapped index)
  // deliberately keeps identity so `step === emitStep` below can tell rows
  // apart without a second render pass.
  const emitStep = steps.find((s) => s.actionType === "EMIT_EVENT");
  const selectedMetric = emitStep && metrics ? metrics.find((m) => m.name === emitStep.vendorActionName) : null;
  const selectedMetricId = selectedMetric ? selectedMetric.id : null;

  // payload_spec keys are suggested from the selected metric's previously
  // seen properties -- sampled, so [] is an expected outcome (see
  // klaviyoPicklists.getMetricProperties), not an error.
  const { data: metricProperties, error: metricPropertiesError } = useBlustreamApiQuery(
    (signal) =>
      selectedMetricId != null
        ? klaviyoPicklists.getMetricProperties(selectedMetricId, { signal })
        : Promise.resolve(null),
    [selectedMetricId, metricPropertiesRetryKey]
  );
  const metricPropertyOptions = (metricProperties || []).map((p) => ({ value: p }));

  useEffect(() => {
    if (visible) {
      setVendorActionNameError(null);
      setPayloadFieldErrors({});
      if (editMapping) {
        form.setFieldsValue({
          bluestreamEventType: editMapping.bluestream_event_type,
        });
        setEnabled(editMapping.enabled !== false);
        if (!isEditableShape(editMapping.steps)) {
          // Unsupported shape -- rendered read-only below. Nothing to parse
          // into editable rows since the editor won't offer Save.
          return;
        }
        if (editMapping.steps?.length > 0) {
          setSteps(editMapping.steps.map((s) => {
            const params = s.params || {};
            return {
              actionType: s.action_type || "EMIT_EVENT",
              vendorActionName: params.vendor_action_name || "",
              payloadSpec: params.payload_spec
                ? Object.entries(params.payload_spec).map(([key, value]) => ({ key, value }))
                : [{ key: "", value: "" }],
              providerField: params.provider_field || "",
              valuePath: params.value_expression || "",
              delayMs: s.delay_ms ?? DEFAULT_DELAY_MS,
              retryAttempts: s.retry_attempts ?? DEFAULT_RETRY_ATTEMPTS,
              retryIntervalMs: s.retry_interval_ms ?? DEFAULT_RETRY_INTERVAL_MS,
              stepEnabled: s.enabled !== false,
            };
          }));
        } else {
          setSteps([emptyStep()]);
        }
      } else {
        form.resetFields();
        setSteps([emptyStep()]);
        setEnabled(true);
      }
    }
  }, [visible, editMapping, form]);

  const updateStep = (index, field, value) => {
    setSteps((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addStep = () => {
    setSteps((prev) => [...prev, emptyStep()]);
  };

  const removeStep = (index) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const moveStep = (index, direction) => {
    setSteps((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return next;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const updatePayloadField = (stepIndex, fieldIndex, prop, value) => {
    setSteps((prev) => {
      const next = [...prev];
      const spec = [...next[stepIndex].payloadSpec];
      spec[fieldIndex] = { ...spec[fieldIndex], [prop]: value };
      next[stepIndex] = { ...next[stepIndex], payloadSpec: spec };
      return next;
    });
  };

  const addPayloadField = (stepIndex) => {
    setSteps((prev) => {
      const next = [...prev];
      next[stepIndex] = {
        ...next[stepIndex],
        payloadSpec: [...next[stepIndex].payloadSpec, { key: "", value: "" }],
      };
      return next;
    });
  };

  const removePayloadField = (stepIndex, fieldIndex) => {
    setSteps((prev) => {
      const next = [...prev];
      next[stepIndex] = {
        ...next[stepIndex],
        payloadSpec: next[stepIndex].payloadSpec.filter((_, i) => i !== fieldIndex),
      };
      return next;
    });
  };

  // Field-level errors come back as steps[].params.payload_spec.<key> or
  // steps[].params.vendor_action_name (EventMappingService#validatePayloadSpec,
  // #validateEmitEventStep) -- parsed here so they land on the offending row
  // instead of a bare toast that leaves the client guessing which of several
  // rows is wrong.
  const applyFieldErrors = (err) => {
    const details = err instanceof ApiError ? err.details : null;
    if (!Array.isArray(details) || details.length === 0) return false;

    const nextPayloadErrors = {};
    let nextVendorError = null;
    let matched = false;

    details.forEach((detail) => {
      const field = detail.rejectedField || detail.rejectedfield || detail.field || "";
      const specMatch = field.match(/^steps\[\]\.params\.payload_spec\.(.+)$/);
      if (specMatch) {
        nextPayloadErrors[specMatch[1]] = detail.message;
        matched = true;
      } else if (field === "steps[].params.vendor_action_name") {
        nextVendorError = detail.message;
        matched = true;
      }
    });

    setPayloadFieldErrors(nextPayloadErrors);
    setVendorActionNameError(nextVendorError);
    return matched;
  };

  const handleSave = async () => {
    setVendorActionNameError(null);
    setPayloadFieldErrors({});
    try {
      const values = await form.validateFields();
      setSaving(true);

      const mappingSteps = steps.map((s, idx) => {
        const params = {};
        if (s.actionType === "EMIT_EVENT") {
          params.vendor_action_name = s.vendorActionName;
          const spec = {};
          s.payloadSpec.forEach((row) => {
            if (row.key.trim()) spec[row.key.trim()] = row.value;
          });
          params.payload_spec = spec;
        } else if (s.actionType === "WRITE_ATTRIBUTE") {
          // StepExecutor's event-mapping WRITE_ATTRIBUTE branch reads
          // provider_field/value_expression from params, same as EMIT_EVENT
          // reads vendor_action_name/payload_spec from there.
          params.provider_field = s.providerField;
          params.value_expression = s.valuePath;
        }
        return {
          step_order: idx + 1,
          action_type: s.actionType,
          delay_ms: s.delayMs,
          retry_attempts: s.retryAttempts,
          retry_interval_ms: s.retryIntervalMs,
          enabled: s.stepEnabled,
          params,
        };
      });

      const body = {
        bluestream_event_type: values.bluestreamEventType,
        enabled,
        steps: mappingSteps,
      };
      // vendor_action_name is deliberately never sent at the mapping level --
      // the server derives it from the EMIT_EVENT step so the list display
      // can't disagree with what's actually sent.

      if (isEdit) {
        await updateEventMapping(editMapping.event_mapping_id, body);
        message.success("Event mapping updated");
      } else {
        await createEventMapping(body);
        message.success("Event mapping created");
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err?.errorFields) return; // form validation
      setSaving(false);
      if (applyFieldErrors(err)) {
        message.error("Fix the highlighted fields and try again");
        return;
      }
      message.error(toUserMessages(err)[0] || "Failed to save event mapping");
      return;
    }
    setSaving(false);
  };

  if (unsupportedShape) {
    const shapeSummary = editMapping.steps.map((s) => s.action_type).join(" → ");
    return (
      <Drawer
        title="Edit Event Mapping"
        placement="right"
        width={600}
        open={visible}
        onClose={onClose}
        footer={
          <div className="drawer-footer" style={{ position: "relative" }}>
            <Button className="standard-button secondary-orange-button" onClick={onClose}>
              Close
            </Button>
          </div>
        }
      >
        <p>
          This mapping&rsquo;s step sequence ({shapeSummary}) cannot be edited here. The editor
          only supports an optional run of WRITE_ATTRIBUTE steps followed by exactly one
          EMIT_EVENT step. Editing and saving it in this drawer would silently drop the steps
          that don&rsquo;t fit that shape, so saving is disabled for this mapping.
        </p>
      </Drawer>
    );
  }

  return (
    <Drawer
      title={isEdit ? "Edit Event Mapping" : "Add Event Mapping"}
      placement="right"
      width={600}
      open={visible}
      onClose={onClose}
      footer={
        <div className="drawer-footer" style={{ position: "relative" }}>
          <Button className="standard-button secondary-orange-button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="standard-button primary-orange-button"
            loading={saving}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Trigger Event"
          name="bluestreamEventType"
          rules={[{ required: true, message: "Enter a trigger event" }]}
        >
          <AutoComplete
            placeholder="e.g., polly.conversation_closed"
            className="standard-input"
            disabled={isEdit}
            options={KNOWN_EVENT_TYPES}
            filterOption={(inputValue, option) =>
              option.value.toLowerCase().includes(inputValue.toLowerCase())
            }
          />
        </Form.Item>
      </Form>

      <div className="steps-section-heading">Steps</div>

      {steps.map((step, index) => (
        <div key={index} className="step-card">
          <div className="step-card-header">
            <span className="step-badge">{index + 1}</span>
            <div className="step-actions">
              <Button
                size="small"
                icon={<UpOutlined />}
                disabled={index === 0}
                onClick={() => moveStep(index, -1)}
              />
              <Button
                size="small"
                icon={<DownOutlined />}
                disabled={index === steps.length - 1}
                onClick={() => moveStep(index, 1)}
              />
              {steps.length > 1 && (
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => removeStep(index)}
                />
              )}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Action Type</label>
            <Select
              value={step.actionType}
              onChange={(val) => updateStep(index, "actionType", val)}
              style={{ width: "100%", marginTop: 4 }}
              className="standard-input"
            >
              <Option value="EMIT_EVENT">EMIT_EVENT</Option>
              <Option value="WRITE_ATTRIBUTE">WRITE_ATTRIBUTE</Option>
            </Select>
          </div>

          {step.actionType === "EMIT_EVENT" && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Vendor Action Name</label>
                <AutoComplete
                  id={`vendorActionName-${index}`}
                  className="standard-input"
                  value={step.vendorActionName}
                  onChange={(value) => updateStep(index, "vendorActionName", value)}
                  placeholder="e.g., Polly Conversation Closed"
                  style={{ marginTop: 4, width: "100%" }}
                  status={vendorActionNameError ? "error" : undefined}
                  options={metricOptions}
                  // Force closed when there's nothing to suggest -- an empty
                  // "no data" panel would read as an error, not "type your own".
                  open={metricOptions.length > 0 ? undefined : false}
                  filterOption={(inputValue, option) =>
                    option.value.toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
                <div className="field-note-text" style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                  Showing action names Klaviyo currently knows about -- type a new one if a client
                  hasn&rsquo;t emitted it yet.
                </div>
                {vendorActionNameError && (
                  <div className="field-error-text" style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
                    {vendorActionNameError}
                  </div>
                )}
                {step === emitStep && (
                  <PicklistLoadState
                    error={metricsError}
                    onRetry={() => setMetricsRetryKey((k) => k + 1)}
                  />
                )}
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Payload Spec</label>
                {step.payloadSpec.map((row, fi) => {
                  const rowError = row.key.trim() ? payloadFieldErrors[row.key.trim()] : null;
                  // Suggestions only apply to the one recognised EMIT_EVENT
                  // step -- keyOptions stays [] for any other row, which
                  // just means that row's Key field behaves as plain free
                  // text (AutoComplete with no options never blocks typing).
                  const keyOptions = step === emitStep ? metricPropertyOptions : [];
                  return (
                    <div key={fi}>
                      <div className="payload-row" style={{ marginTop: fi === 0 ? 4 : 0 }}>
                        <AutoComplete
                          id={`payloadKey-${index}-${fi}`}
                          className="standard-input"
                          placeholder="Key"
                          value={row.key}
                          onChange={(value) => updatePayloadField(index, fi, "key", value)}
                          status={rowError ? "error" : undefined}
                          options={keyOptions}
                          // Belt-and-suspenders alongside rc-select's own
                          // "don't open a combobox dropdown with zero options
                          // and no notFoundContent" default: explicit here so
                          // the behavior doesn't silently depend on nobody
                          // ever adding a notFoundContent to this field later.
                          open={keyOptions.length > 0 ? undefined : false}
                          filterOption={(inputValue, option) =>
                            option.value.toLowerCase().includes(inputValue.toLowerCase())
                          }
                        />
                        <Input
                          className="standard-input"
                          placeholder="Value / JSONPath"
                          value={row.value}
                          onChange={(e) => updatePayloadField(index, fi, "value", e.target.value)}
                          status={rowError ? "error" : undefined}
                        />
                        {step.payloadSpec.length > 1 && (
                          <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => removePayloadField(index, fi)}
                          />
                        )}
                      </div>
                      {rowError && (
                        <div className="field-error-text" style={{ color: "#ff4d4f", fontSize: 12 }}>
                          {rowError}
                        </div>
                      )}
                    </div>
                  );
                })}
                {step === emitStep && (
                  <>
                    <PicklistLoadState
                      error={metricPropertiesError}
                      onRetry={() => setMetricPropertiesRetryKey((k) => k + 1)}
                    />
                    {!metricPropertiesError &&
                      selectedMetricId != null &&
                      metricProperties &&
                      metricProperties.length === 0 && (
                        <div className="field-note-text" style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                          Klaviyo hasn&rsquo;t reported any properties for this metric yet -- you can
                          still type a key directly.
                        </div>
                      )}
                  </>
                )}
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() => addPayloadField(index)}
                  style={{ paddingLeft: 0, marginTop: 4 }}
                >
                  Add Field
                </Button>
              </div>
            </>
          )}

          {step.actionType === "WRITE_ATTRIBUTE" && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Provider Field</label>
                <Input
                  className="standard-input"
                  value={step.providerField}
                  onChange={(e) => updateStep(index, "providerField", e.target.value)}
                  placeholder="Klaviyo profile property name"
                  style={{ marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Value Path</label>
                <Input
                  className="standard-input"
                  value={step.valuePath}
                  onChange={(e) => updateStep(index, "valuePath", e.target.value)}
                  placeholder="e.g., $.sentiment_overall"
                  style={{ marginTop: 4 }}
                />
              </div>
            </>
          )}
        </div>
      ))}

      <Button type="dashed" block icon={<PlusOutlined />} onClick={addStep}>
        Add Step
      </Button>

      <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 12 }}>
        <label style={{ fontSize: 13, fontWeight: 500 }}>Enabled</label>
        <Switch checked={enabled} onChange={setEnabled} />
      </div>
    </Drawer>
  );
}
